import type { HTMLAttributes, ReactNode } from 'react'
import { Pill, type Tone } from './Pill'
import { Spinner } from './Spinner'
import { cx } from './cx'
import s from './ServerCheck.module.css'

/**
 * Where an asynchronous check stands. `idle` has not run; `checking` is in
 * flight; `ok` passed; `failed` answered NO; `unavailable` could not answer —
 * which is not a no, and is never drawn as one.
 */
export type ServerCheckStatus = 'idle' | 'checking' | 'ok' | 'failed' | 'unavailable'

export interface ServerCheckProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** What is checked: "VAT check", "Company register". */
  label: ReactNode
  status: ServerCheckStatus
  /** The status in words, as the host's source says it ("Verified", "Not
   *  found", "Service unavailable"). Defaults per status. */
  statusLabel?: ReactNode
  /** The pill's tone, when the host's source grades more finely than the five
   *  statuses (a format-only pass is not a registry match). */
  tone?: Tone
  /** The line beside the status: what matched, what the service said. */
  detail?: ReactNode
  /** The act at the row's end — "Verify now", "Retry". The host passes it only
   *  when the caller may take it. */
  action?: ReactNode
}

const DEFAULT_LABEL: Record<ServerCheckStatus, string> = {
  idle: 'Not checked',
  checking: 'Checking…',
  ok: 'Verified',
  failed: 'Not found',
  unavailable: 'Service unavailable',
}

const TONE: Record<Exclude<ServerCheckStatus, 'checking'>, Tone> = {
  idle: 'neutral', ok: 'good', failed: 'critical', unavailable: 'warn',
}

/**
 * A SERVER CHECK, REPORTED ON THE ROW THAT TRIGGERED IT (#2406, "Input
 * Controls and Validation": "VAT check · Checking with VIES… / Verified /
 * Not found · Retry / Service unavailable — does not block saving").
 *
 * The third validation level, and the one that is ASYNCHRONOUS: the answer
 * arrives after the person has moved on. So the status is a polite live region
 * — "Checking…", then "Verified, BE 0403 091 220" — spoken when it changes,
 * without taking focus from wherever the person now is. Checking is a spinner
 * AND words; every settled state is a pill whose words carry the meaning, so
 * the tone is never the only cue.
 *
 * `unavailable` is its own state because the most common answer from a
 * registry, in a browser, is no answer at all — and "the service did not
 * reply" drawn as a red "not found" is a false accusation about the data.
 */
export function ServerCheck({
  label, status, statusLabel, tone, detail, action, className, ...rest
}: ServerCheckProps) {
  const words = statusLabel ?? DEFAULT_LABEL[status]
  return (
    <div className={cx(s.row, className)} data-server-check={status} {...rest}>
      <span className={s.key}>{label}</span>
      <span className={s.value} role="status">
        {status === 'checking'
          ? <span className={s.checking}>
              {/* The words are the status; the spinner's own "Loading" region
                  would be a second, vaguer one inside this. */}
              <span aria-hidden="true"><Spinner size={14} /></span>
              <span>{words}</span>
            </span>
          : <Pill tone={tone ?? TONE[status]}>{words}</Pill>}
        {detail != null && detail !== false && detail !== '' && <span className={s.detail}>{detail}</span>}
      </span>
      {action != null && <span className={s.act}>{action}</span>}
    </div>
  )
}
